
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import EmailForm from './EmailForm';
import SuccessState from './SuccessState';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from '@/hooks/use-toast';

const CompleteContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<'email' | 'success'>('email');
  const [error, setError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  
  // Check for email in URL (returning from email verification)
  useEffect(() => {
    // Simulate loading to prevent flicker
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);

    const params = new URLSearchParams(location.search);
    const emailFromUrl = params.get('email');
    const errorMsg = params.get('error');
    
    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
      toast({
        variant: "destructive",
        title: "Error",
        description: decodeURIComponent(errorMsg),
      });
    }
    
    if (emailFromUrl) {
      setEmail(emailFromUrl);
      toast({
        title: "Email detected",
        description: `Using email: ${emailFromUrl}`,
      });
    }
    
    // Check if quiz has been completed
    const hasCompletedQuiz = localStorage.getItem('hasCompletedQuiz');
    if (!hasCompletedQuiz) {
      toast({
        variant: "destructive",
        title: "Quiz not completed",
        description: "You need to complete the quiz first.",
      });
      navigate('/quiz');
    }

    return () => clearTimeout(timer);
  }, [location, navigate]);
  
  const handleSkip = () => {
    // Set a flag in session storage to indicate the user skipped email entry
    sessionStorage.setItem('skipLogin', 'true');
    
    // Set a flag that we're viewing results
    localStorage.setItem('hasViewedResults', 'true');
    
    toast({
      title: "Continuing without an account",
      description: "Your results won't be saved for future reference.",
    });
    
    // Navigate directly to results
    navigate('/results');
  };

  const handleSuccess = () => {
    setMode('success');
    
    // Set a flag that we're viewing results
    localStorage.setItem('hasViewedResults', 'true');
    
    // Navigate to results after a short delay
    setTimeout(() => {
      navigate('/results');
    }, 2000);
  };
  
  const handleError = (errorMessage: string) => {
    setError(errorMessage);
    setMode('success'); // Show the error state in SuccessState
  };

  if (isLoading) {
    return (
      <div className="max-w-md mx-auto px-4 py-12 flex justify-center">
        <div className="w-10 h-10 border-4 border-morgenBlue border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-md mx-auto px-4 py-12"
    >
      <Card className="shadow-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Assessment Complete!</CardTitle>
          <CardDescription>
            {mode === 'email' ? 
              "Enter your email to save your results and see your personalized insights." :
              error ? "Error occurred" : "Success! Taking you to your results."}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && mode === 'email' && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {mode === 'email' && (
            <EmailForm 
              email={email}
              setEmail={setEmail}
              onSkip={handleSkip}
              isSubmitting={isSubmitting}
              setIsSubmitting={setIsSubmitting}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}
          
          {mode === 'success' && <SuccessState error={error} />}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CompleteContent;
